import React, { useEffect, useRef, useState } from "react";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  LineElement,
  LineController,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import { Chart } from "react-chartjs-2";

import apiService from "../services/api";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  LineElement,
  LineController,
  PointElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const StackedChart = ({ isDarkMode }) => {
  const chartRef = useRef(null);
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("today"); // 'today', 'last30days', 'last1year'
  const [reportingTimeZone, setReportingTimeZone] =
    useState("America/New_York");
  const [visibleDatasets, setVisibleDatasets] = useState({
    newCustomers: true,
    premiumCustomers: true,
    income: true,
  });
  const visibleDatasetsRef = useRef(visibleDatasets);
  const isMobile = window.innerWidth < 768;

  const toggleDataset = (dataset) => {
    setVisibleDatasets((prev) => {
      const newState = {
        ...prev,
        [dataset]: !prev[dataset],
      };

      visibleDatasetsRef.current = newState;

      // Update chart natively
      if (chartRef.current) {
        const chart = chartRef.current;
        const datasetIndex = [
          "newCustomers",
          "premiumCustomers",
          "income",
        ].indexOf(dataset);

        if (datasetIndex !== -1 && chart.data.datasets[datasetIndex]) {
          chart.data.datasets[datasetIndex].hidden = !newState[dataset];
          chart.update();
        }
      }

      return newState;
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch pre-calculated chart data from new API
        const response = await apiService.getChartData(timeRange);

        if (response && response.data) {
          const { labels, newCustomers, premiumCustomers, income, timeZone } =
            response.data;

          setReportingTimeZone(timeZone || "America/New_York");

          const allDatasets = [
            {
              type: "line",
              label: "New Customers",
              data: newCustomers,
              borderColor: "rgba(156, 163, 175, 1)",
              backgroundColor: "rgba(156, 163, 175, 0.1)",
              borderWidth: 2,
              fill: true,
              tension: 0.4,
              pointRadius: 3,
              pointBackgroundColor: "rgba(156, 163, 175, 1)",
              pointBorderColor: "#fff",
              pointBorderWidth: 1,
              pointHoverRadius: 5,
              pointHoverBackgroundColor: "rgba(156, 163, 175, 1)",
              pointHoverBorderColor: "#fff",
              pointHoverBorderWidth: 2,
              order: 3,
              key: "newCustomers",
            },
            {
              type: "bar",
              label: "Premium Customers",
              data: premiumCustomers,
              backgroundColor: "rgba(100, 149, 237, 0.7)",
              borderColor: "rgba(100, 149, 237, 0.7)",
              borderWidth: 0,
              stack: "Stack 0",
              order: 2,
              key: "premiumCustomers",
            },
            {
              type: "line",
              label: "Income",
              data: income,
              borderColor: "rgba(34, 197, 94, 1)",
              backgroundColor: "rgba(34, 197, 94, 0.1)",
              borderWidth: 2,
              fill: true,
              tension: 0.4,
              pointRadius: 3,
              pointBackgroundColor: "rgba(34, 197, 94, 1)",
              pointBorderColor: "#fff",
              pointBorderWidth: 1,
              pointHoverRadius: 5,
              pointHoverBackgroundColor: "rgba(34, 197, 94, 1)",
              pointHoverBorderColor: "#fff",
              pointHoverBorderWidth: 2,
              order: 1,
              yAxisID: "y1",
              key: "income",
            },
          ];

          setChartData({
            labels,
            datasets: allDatasets.map((dataset) => ({
              ...dataset,
              hidden: !visibleDatasetsRef.current[dataset.key],
            })),
          });
        }
      } catch (error) {
        console.error("Failed to fetch chart data:", error);
        // Set empty data on error
        const defaultLabels =
          timeRange === "today"
            ? [
                "12am",
                "1am",
                "2am",
                "3am",
                "4am",
                "5am",
                "6am",
                "7am",
                "8am",
                "9am",
                "10am",
                "11am",
                "12pm",
                "1pm",
                "2pm",
                "3pm",
                "4pm",
                "5pm",
                "6pm",
                "7pm",
                "8pm",
                "9pm",
                "10pm",
                "11pm",
              ]
            : timeRange === "last30days"
              ? Array.from({ length: 30 }, (_, i) => {
                  const date = new Date();

                  date.setDate(date.getDate() - (29 - i));

                  return `${date.getMonth() + 1}/${date.getDate()}`;
                })
              : [
                  "January",
                  "February",
                  "March",
                  "April",
                  "May",
                  "June",
                  "July",
                  "August",
                  "September",
                  "October",
                  "November",
                  "December",
                ];
        const emptyData = new Array(defaultLabels.length).fill(0);

        const allDatasets = [
          {
            type: "line",
            label: "New Customers",
            data: emptyData,
            borderColor: "rgba(156, 163, 175, 1)",
            backgroundColor: "rgba(156, 163, 175, 0.1)",
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointRadius: 3,
            pointBackgroundColor: "rgba(156, 163, 175, 1)",
            pointBorderColor: "#fff",
            pointBorderWidth: 1,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: "rgba(156, 163, 175, 1)",
            pointHoverBorderColor: "#fff",
            pointHoverBorderWidth: 2,
            order: 3,
            key: "newCustomers",
          },
          {
            type: "bar",
            label: "Premium Customers",
            data: emptyData,
            backgroundColor: "rgba(100, 149, 237, 0.7)",
            borderColor: "rgba(100, 149, 237, 0.7)",
            borderWidth: 0,
            stack: "Stack 0",
            order: 2,
            key: "premiumCustomers",
          },
          {
            type: "line",
            label: "Income",
            data: emptyData,
            borderColor: "rgba(34, 197, 94, 1)",
            backgroundColor: "rgba(34, 197, 94, 0.1)",
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointRadius: 3,
            pointBackgroundColor: "rgba(34, 197, 94, 1)",
            pointBorderColor: "#fff",
            pointBorderWidth: 1,
            pointHoverRadius: 5,
            pointHoverBackgroundColor: "rgba(34, 197, 94, 1)",
            pointHoverBorderColor: "#fff",
            pointHoverBorderWidth: 2,
            order: 1,
            yAxisID: "y1",
            key: "income",
          },
        ];

        setChartData({
          labels: defaultLabels,
          datasets: allDatasets.map((dataset) => ({
            ...dataset,
            hidden: !visibleDatasetsRef.current[dataset.key],
          })),
        });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [isMobile, timeRange]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: isDarkMode
          ? "rgba(31, 41, 55, 0.95)"
          : "rgba(255, 255, 255, 0.95)",
        titleColor: isDarkMode ? "#f3f4f6" : "#111827",
        bodyColor: isDarkMode ? "#d1d5db" : "#4b5563",
        borderColor: isDarkMode
          ? "rgba(75, 85, 99, 0.3)"
          : "rgba(229, 231, 235, 1)",
        borderWidth: 1,
        padding: 12,
        displayColors: true,
        usePointStyle: true,
        boxWidth: 8,
        boxHeight: 8,
        boxPadding: 4,
        callbacks: {
          title(context) {
            return context[0].label;
          },
          label(context) {
            const label = context.dataset.label || "";
            const value = context.parsed.y;

            if (context.dataset.label === "Income") {
              return `Income: $${value.toLocaleString()}`;
            } else if (
              context.dataset.label === "New Customers" ||
              context.dataset.label === "Premium Customers"
            ) {
              return `${label}: ${value}`;
            }

            return `${label}: ${value}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: isDarkMode ? "#9ca3af" : "#6b7280",
          font: {
            size: isMobile ? 9 : 11,
            family: "'Inter', sans-serif",
          },
          maxRotation: timeRange === "last30days" && !isMobile ? 45 : 0,
          minRotation: timeRange === "last30days" && !isMobile ? 45 : 0,
        },
      },
      y: {
        stacked: true,
        beginAtZero: true,
        position: "left",
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: isDarkMode ? "#9ca3af" : "#6b7280",
          font: {
            size: isMobile ? 9 : 11,
            family: "'Inter', sans-serif",
          },
          callback(value) {
            return value;
          },
          maxTicksLimit: isMobile ? 5 : 8,
        },
      },
      y1: {
        beginAtZero: true,
        position: "right",
        grid: {
          display: false,
          drawBorder: false,
        },
        ticks: {
          color: isDarkMode ? "#9ca3af" : "#6b7280",
          font: {
            size: isMobile ? 9 : 11,
            family: "'Inter', sans-serif",
          },
          callback(value) {
            if (value >= 1000) {
              return `$${(value / 1000).toFixed(0)}k`;
            }

            return `$${value}`;
          },
          maxTicksLimit: isMobile ? 5 : 8,
        },
      },
    },
    animation: {
      duration: 1000,
      easing: "easeInOutQuart",
    },
  };

  useEffect(() => {
    const chart = chartRef.current;

    return () => {
      if (chart) {
        chart.destroy();
      }
    };
  }, []);

  if (loading || !chartData) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full min-h-0 flex-col">
      {/* Legend and Time Range Tabs */}
      <div className="mb-2 flex flex-shrink-0 flex-col gap-2 sm:mb-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        {/* Legend */}
        <div className="flex flex-wrap gap-2 sm:gap-6">
          <button
            className={`flex items-center gap-1.5 rounded-lg px-2 py-1 transition-all sm:gap-2 sm:px-3 sm:py-1.5 ${
              visibleDatasets.newCustomers
                ? "bg-gray-100 dark:bg-gray-800"
                : "opacity-50 hover:opacity-75"
            }`}
            onClick={() => toggleDataset("newCustomers")}
          >
            <div className="h-3 w-3 rounded bg-gray-400 sm:h-4 sm:w-4" />
            <span className="text-[10px] text-gray-600 dark:text-gray-400 sm:text-xs">
              New Customers
            </span>
            <i
              className={`fas ${visibleDatasets.newCustomers ? "fa-check text-green-500" : "fa-times text-red-500"} text-[10px] sm:text-xs`}
            />
          </button>
          <button
            className={`flex items-center gap-1.5 rounded-lg px-2 py-1 transition-all sm:gap-2 sm:px-3 sm:py-1.5 ${
              visibleDatasets.premiumCustomers
                ? "bg-gray-100 dark:bg-gray-800"
                : "opacity-50 hover:opacity-75"
            }`}
            onClick={() => toggleDataset("premiumCustomers")}
          >
            <div className="h-3 w-3 rounded bg-blue-500 sm:h-4 sm:w-4" />
            <span className="text-[10px] text-gray-600 dark:text-gray-400 sm:text-xs">
              Premium Customers
            </span>
            <i
              className={`fas ${visibleDatasets.premiumCustomers ? "fa-check text-green-500" : "fa-times text-red-500"} text-[10px] sm:text-xs`}
            />
          </button>
          <button
            className={`flex items-center gap-1.5 rounded-lg px-2 py-1 transition-all sm:gap-2 sm:px-3 sm:py-1.5 ${
              visibleDatasets.income
                ? "bg-gray-100 dark:bg-gray-800"
                : "opacity-50 hover:opacity-75"
            }`}
            onClick={() => toggleDataset("income")}
          >
            <div className="h-3 w-3 rounded bg-green-500 sm:h-4 sm:w-4" />
            <span className="text-[10px] text-gray-600 dark:text-gray-400 sm:text-xs">
              Income
            </span>
            <i
              className={`fas ${visibleDatasets.income ? "fa-check text-green-500" : "fa-times text-red-500"} text-[10px] sm:text-xs`}
            />
          </button>
        </div>

        {/* Time Range Tabs */}
        <div className="flex flex-shrink-0 items-center gap-1.5 sm:gap-2">
          <span
            className="rounded-md bg-gray-100 px-2 py-1 text-[10px] font-medium text-gray-500 dark:bg-gray-800 dark:text-gray-400 sm:text-xs"
            title={reportingTimeZone}
          >
            <span className="sm:hidden">ET</span>
            <span className="hidden sm:inline">Eastern Time</span>
          </span>
          <button
            className={`rounded-lg px-2 py-1 text-[11px] transition-colors sm:px-3 sm:py-1.5 sm:text-sm ${
              timeRange === "today"
                ? "bg-primary-600 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            }`}
            onClick={() => setTimeRange("today")}
          >
            Today
          </button>
          <button
            className={`rounded-lg px-2 py-1 text-[11px] transition-colors sm:px-3 sm:py-1.5 sm:text-sm ${
              timeRange === "last30days"
                ? "bg-primary-600 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            }`}
            onClick={() => setTimeRange("last30days")}
          >
            Last 30 Days
          </button>
          <button
            className={`rounded-lg px-2 py-1 text-[11px] transition-colors sm:px-3 sm:py-1.5 sm:text-sm ${
              timeRange === "last1year"
                ? "bg-primary-600 text-white"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
            }`}
            onClick={() => setTimeRange("last1year")}
          >
            Last 1 Year
          </button>
        </div>
      </div>

      {/* Chart */}
      <div className="min-h-[180px] flex-1 sm:min-h-0">
        <Chart data={chartData} options={options} ref={chartRef} type="bar" />
      </div>
    </div>
  );
};

export default StackedChart;
